import { ref, onMounted, computed, useTemplateRef, nextTick } from 'vue'
import type { SetupContext } from 'vue'
import type { SearchListPageProps, SearchListPageEmits } from './search-list-page'
import type { ButtonsProps, ButtonProps } from '@el-plus/components/buttons'
import type { FormInstance } from '@el-plus/components/form'
import type { TableInstance } from '@el-plus/components/table'
import type { FormItemProp } from 'element-plus'
import { mergeListByKey } from '@el-plus/utils'
import { useLocale } from '@el-plus/hooks/use-locale'

const getAvailableHeight = (el: HTMLElement): number => {
  const elRect = el.getBoundingClientRect()
  let parent = el.parentElement
  while (parent) {
    const style = getComputedStyle(parent)
    const height = style.height
    if (height && height !== 'auto' && !height.endsWith('%')) {
      const parentRect = parent.getBoundingClientRect()
      const paddingBottom = parseFloat(style.paddingBottom) || 0
      return parentRect.bottom - elRect.top - paddingBottom
    }
    if (parent === document.documentElement || parent === document.body) {
      break
    }
    parent = parent.parentElement
  }
  return window.innerHeight - elRect.top
}

export const useSearchListPage = (
  props: SearchListPageProps,
  config = {},
  emit?: SetupContext<SearchListPageEmits>['emit'],
) => {
  const rootRef = useTemplateRef<HTMLDivElement>('rootRef')
  const topRef = useTemplateRef<HTMLDivElement>('topRef')
  const middleRef = useTemplateRef<HTMLDivElement>('middleRef')
  const { calcTableHeight, tableProps, tableRef } = useTable(props, config, rootRef, topRef, middleRef)
  const { formProps, validate, resetFields, formRef } = useForm(props, {
    calcTableHeight,
  })
  const { buttonsProps, searchButtons, search, reset } = useButtons(props, {
    validate,
    resetFields,
    tableRef,
    emit,
  })
  return {
    formProps,
    tableProps,
    buttonsProps,
    searchButtons,
    search,
    reset,
    tableRef,
    formRef,
    beforeSearch: async () => {
      if (!formRef.value) {
        await nextTick()
      }
      await validate()
    }
  }
}
const useTable = (props: SearchListPageProps, config, rootRef: ReturnType<typeof useTemplateRef<HTMLDivElement>>, topRef: ReturnType<typeof useTemplateRef<HTMLDivElement>>, middleRef: ReturnType<typeof useTemplateRef<HTMLDivElement>>) => {
  const instanceName = 'tableRef'
  const tableRef = useTemplateRef<TableInstance>(instanceName)
  const tableHeight = ref(0)
  const tableProps = computed(() => {
    return {
      ref: instanceName,
      columns: props.columns,
      showPagination: true,
      height: tableHeight.value,
      showIndexCol: props.showIndexCol,
      showSelectionCol: props.showSelectionCol,
      showSingleSelectionCol: props.showSingleSelectionCol,
      actionButtons: props.actionButtons,
      formatColumns: props.formatColumns,
      api: props.api,
      reqAfter: props.reqAfter,
      reqBefore: props.reqBefore,
      reqData: props.formData,
      customColumnModule: props.customColumnModule,
      customColumnApi: props.customColumnApi,
      customColumnSaveApi: props.customColumnSaveApi,
      minWidth: props.minWidth,
      actionColWidth: props.actionColWidth,
      ...props.tableProps,
    }
  })
  const calcTableHeight = () => {
    setTimeout(() => {
      const topHeight = topRef.value?.offsetHeight ?? 0
      const middleHeight = middleRef.value?.offsetHeight ?? 0
      let paginationHeight = 0
      if (tableProps.value.showPagination) {
        const paginationEl = rootRef.value?.querySelector('.el-pagination') as HTMLElement | null
        paginationHeight = paginationEl ? paginationEl.offsetHeight + 12 : 0
      }
      const containerHeight = rootRef.value
        ? getAvailableHeight(rootRef.value)
        : window.innerHeight

      tableHeight.value =
        containerHeight -
        topHeight -
        middleHeight -
        paginationHeight -
        props.offsetTop!
    }, 0)
  }

  onMounted(calcTableHeight)
  return {
    tableRef,
    tableProps,
    calcTableHeight,
  }
}
const useForm = (props: SearchListPageProps, { calcTableHeight }) => {
  const instanceName = 'formRef'
  const formRef = useTemplateRef<FormInstance>(instanceName)
  const formProps = computed(() => {
    return {
      ref: instanceName,
      formItemList: props.formItemList,
      isShowFold: true,
      modelValue: props.formData,
      ...props.formProps,
      onToggleStatus: () => {
        calcTableHeight()
      },
    }
  })
  // 校验
  const validate = () => {
    return formRef.value!.validate()
  }
  // 重置校验
  const resetFields = (props: FormItemProp) => {
    return formRef.value!.resetFields(props)
  }
  // 清理某个字段的表单验证信息
  const clearValidate = (props: FormItemProp) => {
    return formRef.value!.clearValidate(props)
  }
  return {
    formProps,
    formRef,
    validate,
    resetFields,
    clearValidate,
  }
}
const useButtons = (
  props: SearchListPageProps,
  { validate, resetFields, tableRef, emit },
) => {
  const { t } = useLocale()
  const defaultButtons: ButtonProps<any[]>[] = [
    {
      name: t('ep.searchListPage.add'),
      prop: 'add',
      permission: props.name ? `${props.name}:ADD` : '',
      show: () => {
        return !!props.add || !!props.leftButtons?.some((item) => item.prop === 'add')
      },
      type: 'primary',
      onClick: () => {
        props.add?.()
      },
    },
    // {
    //   name: t('ep.searchListPage.templateDownload'),
    //   prop: 'templateDownload',
    //   permission: props.name ? `${props.name}:TEMPLATEDOWNLOAD` : '',
    //   show: !!props.templateDownloadApi,
    // },
    // {
    //   name: t('ep.searchListPage.import'),
    //   prop: 'import',
    //   permission: props.name ? `${props.name}:IMPORT` : '',
    //   show: !!props.importApi,
    // },
    // {
    //   name: t('ep.searchListPage.export'),
    //   prop: 'export',
    //   permission: props.name ? `${props.name}:EXPORT` : '',
    //   show: !!props.exportApi,
    // },
  ]
  const list = mergeListByKey(defaultButtons, props.leftButtons)
  if (props.customColumnModule) {
    list.push({
      name: t('ep.customColumn.columnSettings'),
      prop: 'customColumn',
      type: 'primary',
      onClick: () => {
        tableRef.value.openCustomColumnDialog()
      },
    })
  }
  list.forEach((item: ButtonProps<any[]>) => {
    if (item.onClick) {
      const clickFn = item.onClick
      item.onClick = () => {
        const rows = tableRef.value.elTableRef.getSelectionRows()
        clickFn(rows)
      }
    }
    if (item.confirmBefore) {
      const confirmBeforeFn = item.confirmBefore
      item.confirmBefore = async () => {
        const rows = tableRef.value.elTableRef.getSelectionRows()
        await confirmBeforeFn(rows)
      }
    }
  })

  const buttonsProps = computed(() => {
    return {
      list,
      ...props.buttonsProps,
    }
  })
  // 搜索
  const search = async (isInitSearch = false) => {
    // await validate()
    // 如果isInitSearch为true+reserveSelection为true，清空选中状态
    if (isInitSearch && props.tableProps?.reserveSelection) {
      tableRef.value.elTableRef.clearSelection()
    }
    tableRef.value.search(isInitSearch)
  }
  // 清空
  const reset = async () => {
    // 清空前钩子，返回 false 阻止清空
    if (props.clearBefore && props.clearBefore() === false) return
    resetFields()
    search(true)
    emit?.('clear')
  }
  const searchButtons: ButtonsProps['list'] = [
    {
      name: t('ep.searchListPage.search'),
      type: 'primary',
      onClick: search.bind(null, true),
    },
    {
      name: t('ep.searchListPage.clear'),
      onClick: reset,
    },
  ]
  return {
    buttonsProps,
    searchButtons,
    search,
    reset,
  }
}
